﻿'use client';

import React from 'react';
import { motion } from 'motion/react';
import { token } from '@/lib/theme/tokens';
import { useThemeVersion } from '@/lib/theme/useThemeColors';

type LoaderSize = 'full' | 'inline' | 'mini';

interface SizeConfig {
  width: number;
  height: number;
  core: number;
  mid: number;
  shadow: number;
  white: number;
  pulse: number;
  sparkles: boolean;
  text: boolean;
}

const SIZES: Record<LoaderSize, SizeConfig> = {
  full: { width: 240, height: 90, core: 4, mid: 8, shadow: 12, white: 1.5, pulse: 3, sparkles: true, text: true },
  inline: { width: 160, height: 60, core: 3, mid: 6, shadow: 10, white: 1, pulse: 2, sparkles: true, text: false },
  mini: { width: 56, height: 24, core: 2, mid: 4, shadow: 6, white: 0.8, pulse: 1.5, sparkles: false, text: false },
};

const SPEEDS = {
  bg: 4.5,
  fg: 3.5,
  pulse: 2,
  float: 3,
};

const SPARKLES = [
  { top: '35%', size: 3, duration: 4, delay: 0 },
  { top: '60%', size: 2, duration: 3.5, delay: 1.5 },
  { top: '45%', size: 4, duration: 5, delay: 0.8 },
  { top: '70%', size: 1.5, duration: 4.2, delay: 2.3 },
];

const PATH_BG = 'M 0 50 C 60 5, 140 5, 200 50 C 260 95, 340 95, 400 50 C 460 5, 540 5, 600 50 C 660 95, 740 95, 800 50';
const PATH_FG = 'M 0 50 C 60 95, 140 95, 200 50 C 260 5, 340 5, 400 50 C 460 95, 540 95, 600 50 C 660 5, 740 5, 800 50';

const uid = (() => {
  let id = 0;
  return () => `vl-${++id}`;
})();

interface LinkLoaderProps {
  size?: LoaderSize;
  title?: string;
  subtitle?: string;
  className?: string;
}

export function LinkLoader({ size = 'full', title, subtitle, className = '' }: LinkLoaderProps) {
  const s = SIZES[size];
  const showText = s.text && (title || subtitle);
  const prefix = React.useMemo(uid, []);

  // Brand emerald ramp from the live theme: deep base + bright glow. Lighter tints, whites,
  // rgba glows and the slate text shimmer are kept to preserve the gradient depth.
  useThemeVersion();
  const brandDeep = token('--primary', '#0d9488');
  const brandBright = token('--accent-brand', '#2dd4bf');

  const gradIds = {
    shadow: `${prefix}-shadow`,
    mid: `${prefix}-mid`,
    core: `${prefix}-core`,
  };

  return (
    <div className={`flex flex-col items-center justify-center gap-3 ${className}`}>
      <style dangerouslySetInnerHTML={{ __html: `
        .vl-mask-${prefix} {
          -webkit-mask-image: linear-gradient(90deg, transparent 0%, black 25%, black 75%, transparent 100%);
          mask-image: linear-gradient(90deg, transparent 0%, black 25%, black 75%, transparent 100%);
        }
        ${showText ? `
        .vl-shimmer-${prefix} {
          background: linear-gradient(90deg, #334155 0%, #94a3b8 50%, #334155 100%);
          background-size: 200% auto;
          color: transparent;
          -webkit-background-clip: text;
          background-clip: text;
          animation: vlShimmer-${prefix} 3s linear infinite;
        }
        @keyframes vlShimmer-${prefix} {
          to { background-position: 200% center; }
        }` : ''}
      `}} />

      <svg width="0" height="0" className="absolute">
        <defs>
          <linearGradient id={gradIds.shadow} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={brandDeep} stopOpacity={0} />
            <stop offset="50%" stopColor={brandDeep} stopOpacity={0.8} />
            <stop offset="100%" stopColor={brandDeep} stopOpacity={0} />
          </linearGradient>
          <linearGradient id={gradIds.mid} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={brandBright} stopOpacity={0.2} />
            <stop offset="50%" stopColor={brandBright} stopOpacity={0.9} />
            <stop offset="100%" stopColor={brandBright} stopOpacity={0.2} />
          </linearGradient>
          <linearGradient id={gradIds.core} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ccfbf1" stopOpacity={0} />
            <stop offset="30%" stopColor="#f0fdfa" stopOpacity={0.8} />
            <stop offset="50%" stopColor="#ffffff" stopOpacity={1} />
            <stop offset="70%" stopColor="#f0fdfa" stopOpacity={0.8} />
            <stop offset="100%" stopColor="#ccfbf1" stopOpacity={0} />
          </linearGradient>
        </defs>
      </svg>

      <motion.div
        className={`relative overflow-hidden vl-mask-${prefix}`}
        style={{ width: s.width, height: s.height }}
        animate={{ y: [-1.5, 1.5] }}
        transition={{ repeat: Infinity, duration: SPEEDS.float, ease: 'easeInOut', repeatType: 'reverse' }}
      >
        {/* Layer 1: Background Ribbon */}
        <motion.svg
          className="absolute top-0 left-0 h-full fill-none opacity-80"
          style={{ width: '200%', filter: 'blur(1.5px)' }}
          viewBox="0 0 800 100"
          preserveAspectRatio="none"
          animate={{ x: ['0%', '-50%'] }}
          transition={{ repeat: Infinity, duration: SPEEDS.bg, ease: 'linear' }}
        >
          <path d={PATH_BG} stroke={`url(#${gradIds.shadow})`} strokeWidth={s.shadow} strokeLinecap="round" />
        </motion.svg>

        {/* Layer 2: Foreground Ribbon */}
        <motion.svg
          className="absolute top-0 left-0 h-full fill-none"
          style={{ width: '200%', filter: 'drop-shadow(0 3px 5px rgba(15,118,110,0.4))' }}
          viewBox="0 0 800 100"
          preserveAspectRatio="none"
          animate={{ x: ['0%', '-50%'] }}
          transition={{ repeat: Infinity, duration: SPEEDS.fg, ease: 'linear' }}
        >
          <path d={PATH_FG} stroke={`url(#${gradIds.mid})`} strokeWidth={s.mid} strokeLinecap="round" />
        </motion.svg>

        {/* Layer 3: Hot Core */}
        <motion.svg
          className="absolute top-0 left-0 h-full fill-none"
          style={{ width: '200%', filter: `drop-shadow(0 0 10px ${brandBright})` }}
          viewBox="0 0 800 100"
          preserveAspectRatio="none"
          animate={{ x: ['0%', '-50%'] }}
          transition={{ repeat: Infinity, duration: SPEEDS.fg, ease: 'linear' }}
        >
          <path d={PATH_FG} stroke={`url(#${gradIds.core})`} strokeWidth={s.core} strokeLinecap="round" />
          <path
            d={PATH_FG}
            stroke="#ffffff"
            strokeWidth={s.white}
            strokeLinecap="round"
            style={{ filter: 'drop-shadow(0 0 4px #fff)' }}
          />
        </motion.svg>

        {/* Layer 4: Energy Pulse */}
        <motion.svg
          className="absolute top-0 left-0 h-full fill-none"
          style={{ width: '200%', mixBlendMode: 'screen' }}
          viewBox="0 0 800 100"
          preserveAspectRatio="none"
          animate={{ x: ['0%', '-50%'] }}
          transition={{ repeat: Infinity, duration: SPEEDS.bg, ease: 'linear' }}
        >
          <motion.path
            d={PATH_BG}
            stroke="#ffffff"
            strokeWidth={s.pulse}
            strokeLinecap="round"
            strokeDasharray="20 780"
            style={{ filter: `drop-shadow(0 0 8px #fff) drop-shadow(0 0 16px ${brandBright})` }}
            animate={{ strokeDashoffset: [800, 0] }}
            transition={{ repeat: Infinity, duration: SPEEDS.pulse, ease: 'linear' }}
          />
        </motion.svg>

        {/* Sparkles */}
        {s.sparkles && (
          <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ mixBlendMode: 'screen' }}>
            {SPARKLES.map((sp, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full"
                style={{
                  top: sp.top,
                  width: sp.size,
                  height: sp.size,
                  background: '#fff',
                  boxShadow: `0 0 8px 2px ${brandBright}, 0 0 16px 4px rgba(45,212,191,0.6)`,
                }}
                initial={{ left: '110%', opacity: 0, scale: 0.5 }}
                animate={{ left: '-10%', opacity: [0, 1, 1, 0], scale: [0.5, 1.5, 1.5, 0.5] }}
                transition={{ repeat: Infinity, duration: sp.duration, ease: 'linear', delay: sp.delay }}
              />
            ))}
          </div>
        )}
      </motion.div>

      {showText && (
        <div className="flex flex-col items-center gap-1">
          {title && (
            <span className={`text-sm font-extrabold tracking-[2px] uppercase vl-shimmer-${prefix}`}>
              {title}
            </span>
          )}
          {subtitle && (
            <span className="text-xs font-medium text-text-tertiary">
              {subtitle}
              <motion.span
                animate={{ opacity: [0, 1, 0] }}
                transition={{ repeat: Infinity, duration: 1.5, ease: 'linear' }}
              >
                ...
              </motion.span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
